// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Notify from "@bg/App/Components/Notify";
import loadSystemList from "@bg/App/Handlers/CommonHandlers/LoadSystemList";
import addNewGroup from "@bg/App/Handlers/CustomQuote/AddBlankGroup";
import addToQuote from "@bg/App/Handlers/CustomQuote/AddNewCustomQuoteLine";
import { CustomQuoteModel } from "@bg/App/Services/CustomQuoteService";
import { CustomAppState } from "@bg/App/Store/Reducers";
import { GroupItem, GroupList } from "@bg/Overrides/Components/AddToListModal";
import {
    myListsDetailsOptionsStyles,
    MyListsDetailsOptionsStyles,
} from "@bg/Overrides/Widgets/MyListsDetails/MyListDetailsOptionsStyles";
import { fontConfig } from "@bg/XDL/PreStyle/typography";
import { updateGroupNameHandler } from "@bg/XDL/Utilities/groupName";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { IconWrapper } from "@insite/mobius/Icon";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Modal from "@insite/mobius/Modal";
import TextField from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import React, { useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    productInfo?: ProductInfo;
    product?: ProductModel;
    creatingNewGroup: boolean;
    groupingModalIsOpen?: boolean;
    closeModalCallback: () => void;
    callAfterSuccess?: (newGroupName: string, isMoving?: boolean) => void;
    isMovingItem?: boolean;
    quote?: CustomQuoteModel;
    showButton?: boolean;
    variant?: "button" | "clickable";
    onClick?: () => void;
}

const mapStateToProps = (state: CustomAppState) => {
    const groupListName = state.context.website.properties["groupsListName"];
    return {
        defaultListGroups: (state.xSystemList as any)[groupListName]?.values,
        uncategorizedGroupName: (getSettingsCollection(state).customSettings as any).unCategorizedGroupName as string,
        addOnsGroupName: (getSettingsCollection(state).customSettings as any).addOnsGroupName as string,
        groupsNamingRegex: (getSettingsCollection(state).customSettings as any).groupsNamingRegex as string,
        groupListName,
    };
};

const mapDispatchToProps = {
    addToQuote,
    loadSystemList,
    addNewGroup,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CreateGroupModel extends MyListsDetailsOptionsStyles {
    createGroupButton?: ButtonPresentationProps;
    createGroupClickable?: ClickablePresentationProps;
}

export const styles: CreateGroupModel = {
    ...myListsDetailsOptionsStyles,
    createGroupButton: {
        css: css`
            margin-top: -10px;
            margin-bottom: 50px;
            padding: 0 12px !important;
            ${IconWrapper} {
                transition: all 200ms ease-in-out;
            }
            &&:hover {
                ${IconWrapper} {
                    color: ${getColor("text.main")};
                }
            }
        `,
        icon: {
            src: "CreateGroup",
            position: "left",
        },
    },
};

const CreateNewGroupModal = ({
    loadSystemList,
    defaultListGroups,
    groupingModalIsOpen,
    creatingNewGroup,
    productInfo,
    product,
    closeModalCallback,
    callAfterSuccess,
    isMovingItem,
    uncategorizedGroupName,
    addOnsGroupName,
    quote,
    addToQuote,
    showButton,
    variant = "button",
    onClick,
    addNewGroup,
    groupListName,
    groupsNamingRegex,
}: Props) => {
    const toasterContext = useContext(ToasterContext);
    const [selectedGroup, setSelectedGroup] = useState("");
    const [currentStep, setCurrentStep] = useState("");
    const [groupName, setGroupName] = useState("");
    const [newGroupError, setNewGroupError] = useState("");
    const [inProgress, setInProgress] = useState(false);
    const [groups, setGroups] = useState<string[]>([]);

    useEffect(() => {
        if (groupingModalIsOpen && (!defaultListGroups || !defaultListGroups.length)) {
            loadSystemList({
                systemListName: groupListName,
            });
        }
        if (groupingModalIsOpen) {
            setCurrentStep("");
            setGroupName("");
            setNewGroupError("");
            setSelectedGroup("");
            setInProgress(false);
        }
    }, [groupingModalIsOpen]);

    useEffect(() => {
        if (quote && quote.groups) {
            const groups = quote.groups.split(",") || "";
            setGroups(groups);
        }
    }, [quote]);

    const modalCloseHandler = () => {
        closeModalCallback();
    };

    const newGroupInputChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setGroupName(event.target.value);
        if (event.target.value) {
            setNewGroupError("");
        }
    };

    const addNewGroupAndGoBack = () => {
        const { newGroupName, error } = updateGroupNameHandler(groupName, groupsNamingRegex, groups, {
            required: "Group Name is Required",
            exists: "Selected group already exists in the list",
            notValid: siteMessage("Custom_Group_Name_Error_Text") as string,
        });

        if (error) {
            setNewGroupError(error);
            return;
        }

        creatingNewGroup ? updateQuote({}, newGroupName) : addItemToQuote({}, newGroupName);
    };

    const handleReturnFromNewGroup = () => {
        setGroupName("");
        setCurrentStep("");
    };

    const handleGroupSelection = (groupName: string) => {
        if (selectedGroup.toLowerCase() === groupName.toLowerCase()) {
            setSelectedGroup("");
        } else {
            setSelectedGroup(groupName);
        }
    };

    if (!quote) {
        return null;
    }

    const updateQuote = (e?: any, newGroup?: string) => {
        const group = selectedGroup || newGroup;
        if (groups.indexOf(group!.toLowerCase()) === -1 && groups.length >= 9) {
            toasterContext.addToast({ body: siteMessage("Custom_MaxAllowedGroups"), messageType: "danger" });
            return;
        }
        if (isMovingItem) {
            callAfterSuccess?.(group!, isMovingItem);
            return;
        }
        if (groups.filter(name => name.toLowerCase() === group!.toLowerCase()).length === 0) {
            setInProgress(true);
            addNewGroup({
                customQuoteId: quote.id,
                newBlankGroupName: group || uncategorizedGroupName,
                onError: e => {
                    onSubmitError(e);
                    setInProgress(false);
                },
                onComplete: (result: any) => {
                    setInProgress(false);
                    if (creatingNewGroup) {
                        callAfterSuccess?.(group!, isMovingItem);
                        modalCloseHandler();
                    }
                    if (result.successful) {
                        toasterContext.addToast({
                            body: translate("Quote Updated"),
                            messageType: "success",
                        });
                    }
                },
            });
        } else {
            toasterContext.addToast({
                body: translate("Selected group already exists in the quote"),
                messageType: "warning",
            });
            setSelectedGroup("");
        }
    };

    const onSubmitError = (errorMessage: any) => {
        toasterContext.addToast({
            body: translate(`Error: ${errorMessage}`),
            messageType: "danger",
        });
    };

    const addItemToQuote = (e?: any, newGroup?: string) => {
        const group = selectedGroup || newGroup;
        if (group && groups.indexOf(group.toLowerCase()) === -1 && groups.length >= 9) {
            toasterContext.addToast({ body: siteMessage("Custom_MaxAllowedGroups"), messageType: "danger" });
            return;
        }
        if (productInfo?.productId && product) {
            setInProgress(true);
            addToQuote({
                customQuoteId: quote.id,
                groupName: group || uncategorizedGroupName,
                linesModel: [
                    {
                        productErpNumber: product.productNumber,
                        qty: productInfo.qtyOrdered,
                    },
                ],
                onComplete(res: any) {
                    modalCloseHandler();
                    setInProgress(false);
                    if (res.successful) {
                        toasterContext.addToast({ body: translate("Item Added"), messageType: "success" });
                        callAfterSuccess?.(group || uncategorizedGroupName, isMovingItem);
                    }
                },
            });
        }
    };

    const addItemCartWithGroup = () => {
        addItemToQuote();
    };

    const title = currentStep === "" ? translate("Choose a Group") : translate("Add a New Group");

    const CreateGroupButton =
        variant === "button" ? (
            <Button
                onClick={onClick}
                css={css`
                    margin-top: -10px;
                    margin-bottom: 50px;
                `}
                key="createNewGroupButton"
                variant="secondary"
                {...styles.createGroupButton}
            >
                {translate("Create New Group")}
            </Button>
        ) : (
            <Clickable {...styles.createGroupClickable} onClick={onClick} key="createNewGroupButton">
                {translate("Create New Group")}
            </Clickable>
        );

    let customListGroupNamesToDispaly;

    if (groups !== undefined && defaultListGroups !== undefined) {
        // groups = list chossen groups.
        const defaultListGroupsNames = defaultListGroups.map((obj: any) => obj.name.toLowerCase());
        const customListGroupNames = groups.filter(
            (name: string) => !defaultListGroupsNames.includes(name.toLowerCase()),
        );
        customListGroupNamesToDispaly = customListGroupNames.filter(
            (name: string) =>
                name.toLowerCase() !== uncategorizedGroupName.toLowerCase() &&
                name.toLowerCase() !== addOnsGroupName.toLowerCase(),
        );
    }

    return (
        <>
            {showButton && CreateGroupButton}
            <Modal
                className="add-top-padding"
                size={812}
                headline={title}
                isOpen={groupingModalIsOpen}
                handleClose={modalCloseHandler}
            >
                <StyledWrapper {...styles.stepWrapper} className={currentStep === "" ? "in-view" : ""}>
                    <StyledWrapper {...styles.groupsWrapper}>
                        {defaultListGroups &&
                            defaultListGroups.length > 0 &&
                            defaultListGroups.map((group: GroupList) => (
                                <Clickable
                                    className={
                                        selectedGroup.toLowerCase() === group.name.toLowerCase() ? "selected" : ""
                                    }
                                    key={group.id}
                                    {...styles.groupItem}
                                    onClick={() => handleGroupSelection(group.name)}
                                >
                                    <GroupItem imagePath={group.additionalInfo} groupName={group.description} />
                                </Clickable>
                            ))}
                        {!isMovingItem &&
                            !creatingNewGroup &&
                            customListGroupNamesToDispaly &&
                            customListGroupNamesToDispaly.length > 0 &&
                            customListGroupNamesToDispaly.map((group: any) => (
                                <Clickable
                                    className={selectedGroup.toLowerCase() === group.toLowerCase() ? "selected" : ""}
                                    key={group}
                                    {...styles.groupItem}
                                    onClick={() => handleGroupSelection(group)}
                                >
                                    <GroupItem imagePath="null" groupName={group} />
                                </Clickable>
                            ))}
                        <StyledWrapper {...styles.groupItem}>
                            <StyledWrapper {...styles.addNewGroupItem}>
                                <Button onClick={() => setCurrentStep("newGroup")} {...styles.addNewButton}>
                                    <ButtonIcon src="Plus" size={16} />
                                </Button>
                            </StyledWrapper>
                            <Typography
                                lineHeight={fontConfig.lineHeight.singleLineSmall}
                                transform="uppercase"
                                color="secondary"
                            >
                                {translate("New Blank Group")}
                            </Typography>
                        </StyledWrapper>
                    </StyledWrapper>
                    <StyledWrapper {...styles.buttonsWrapper} className="modal-actions">
                        <Button
                            {...styles.addButton}
                            onClick={creatingNewGroup ? updateQuote : addItemCartWithGroup}
                            data-test-selector="productAddToQuote_addButton"
                            disabled={inProgress || (creatingNewGroup && !selectedGroup)}
                        >
                            {inProgress ? (
                                <LoadingSpinner color="secondary" size={28} />
                            ) : creatingNewGroup ? (
                                translate("Create Group")
                            ) : (
                                translate("Add Item To Quote")
                            )}{" "}
                        </Button>
                    </StyledWrapper>
                </StyledWrapper>
                <StyledWrapper {...styles.stepWrapper} className={currentStep === "newGroup" ? "in-view" : ""}>
                    <TextField
                        label={translate("Group Name")}
                        value={groupName}
                        error={newGroupError}
                        maxLength={50}
                        onChange={newGroupInputChangeHandler}
                        placeholder={translate("New Group Name")}
                        {...styles.newListInput}
                        data-test-selector="productAddToQuote_newGroupName"
                    />
                    {groupsNamingRegex && (
                        <StyledWrapper {...styles.notifyStyle}>
                            <Notify messageType="info" body={siteMessage("Custom_GroupName_Limitations_Text")} />
                        </StyledWrapper>
                    )}
                    <StyledWrapper {...styles.buttonsWrapper} className="modal-actions">
                        <Button
                            {...styles.addButton}
                            onClick={addNewGroupAndGoBack}
                            data-test-selector="productAddToQuote_saveGroupAndContinue"
                            disabled={inProgress || newGroupError.length > 0}
                        >
                            {translate("Save and Continue")}
                        </Button>
                        <Button {...styles.cancelButton} onClick={handleReturnFromNewGroup}>
                            {translate("Return to Previous Screen")}
                        </Button>
                    </StyledWrapper>
                </StyledWrapper>
            </Modal>
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(CreateNewGroupModal);
